// src/pages/post/PostDetail.jsx
import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { FaTags, FaEye, FaLink, FaShareAlt } from 'react-icons/fa';

import { UserContext } from '../../context/userContext';
import PostAuthor from '../../components/post/PostAuthor';
import PostActions from '../../components/post/PostActions';
import CommentList from '../../components/post/CommentList';
import Loader from '../../components/common/Loader';
import DeletePost from './DeletePost';

const PostDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [copiedLink, setCopiedLink] = useState(false);

  const { currentUser } = useContext(UserContext);

  // Fetch post data
  useEffect(() => {
    const getPost = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/posts/${id}`, {
          headers: currentUser?.token ? { Authorization: `Bearer ${currentUser.token}` } : {}
        });
        setPost(response.data);
        
        // Also fetch related posts
        fetchRelatedPosts(response.data);
      } catch (error) {
        setError(error.message || 'Failed to fetch post');
      }
      setIsLoading(false);
    };
    
    getPost();
  }, [id, currentUser]);

  // Fetch related posts
  const fetchRelatedPosts = async (postData) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/posts/${id}/related`, {
        headers: currentUser?.token ? { Authorization: `Bearer ${currentUser.token}` } : {}
      });
      setRelatedPosts(response.data);
    } catch (error) {
      console.error('Error fetching related posts:', error);
    }
  };

  // Copy post link to clipboard
  const copyPostLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  // Toggle comments section
  const toggleComments = () => {
    setShowComments(prev => !prev);
  };

  // Refresh post data
  const refreshPost = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/posts/${id}`, {
        headers: currentUser?.token ? { Authorization: `Bearer ${currentUser.token}` } : {}
      });
      setPost(response.data);
    } catch (error) {
      console.error('Error refreshing post:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg text-center">
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p>{error}</p>
          <Link to="/" className="mt-4 inline-block px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <article className="py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Post header with author info and actions */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <PostAuthor authorID={post.creator._id} createdAt={post.createdAt} />
          
          {currentUser?.id === post.creator._id && (
            <div className="flex gap-3 mt-4 md:mt-0">
              <Link 
                to={`/posts/${post._id}/edit`} 
                className="px-3 py-1 bg-primary text-white text-sm rounded hover:bg-opacity-90 transition-colors"
              >
                Edit
              </Link>
              <DeletePost postId={id} />
            </div>
          )}
        </div>
        
        {/* Post title */}
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{post.title}</h1>
        
        {/* Post meta information */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Link 
            to={`/posts/categories/${post.category}`} 
            className="inline-flex items-center bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-full text-sm hover:bg-opacity-20 transition-colors"
          >
            {post.category}
          </Link>
          
          {post.tags && post.tags.length > 0 && (
            <div className="flex items-center text-gray-600 text-sm">
              <FaTags className="mr-2" />
              {post.tags.map((tag, index) => (
                <span key={index} className="mr-2">
                  #{tag}
                </span>
              ))}
            </div>
          )}
          
          <div className="flex items-center text-gray-600 text-sm">
            <FaEye className="mr-2" />
            <span>{post.views} views</span>
          </div>
          
          <button 
            className={`flex items-center text-sm ${copiedLink ? 'text-green-600' : 'text-gray-600 hover:text-primary'} transition-colors`} 
            onClick={copyPostLink}
          >
            <FaLink className="mr-2" />
            <span>{copiedLink ? 'Copied!' : 'Copy Link'}</span>
          </button>
        </div>
        
        {/* Post thumbnail */}
        <div className="mb-8 rounded-xl overflow-hidden shadow-md">
          <img 
            src={`${process.env.REACT_APP_ASSETS_URL}/uploads/${post.thumbnail}`} 
            alt={post.title} 
            className="w-full h-auto object-cover"
          />
        </div>
        
        {/* Post content */}
        <div className="prose max-w-none mb-8">
          <div dangerouslySetInnerHTML={{ __html: post.description }}></div>
        </div>
        
        {/* Post media gallery */}
        {post.media && post.media.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-bold mb-4">Gallery</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {post.media.map((media, index) => (
                <div key={index} className="rounded-lg overflow-hidden shadow-sm">
                  <img 
                    src={`${process.env.REACT_APP_ASSETS_URL}/uploads/${media}`} 
                    alt={`Media ${index + 1}`} 
                    className="w-full h-auto object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Post actions */}
        <PostActions 
          post={post} 
          onUpdate={refreshPost} 
          showComments={showComments} 
          toggleComments={toggleComments} 
        />
        
        {/* Comments section */}
        {showComments && <CommentList postId={id} />}
        
        {/* Related posts section */}
        {relatedPosts && relatedPosts.length > 0 && (
          <div className="mt-12">
            <h3 className="text-xl font-bold mb-6">Related Posts</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {relatedPosts.map(relatedPost => (
                <Link 
                  key={relatedPost._id} 
                  to={`/posts/${relatedPost._id}`}
                  className="block bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="h-40 overflow-hidden">
                    <img 
                      src={`${process.env.REACT_APP_ASSETS_URL}/uploads/${relatedPost.thumbnail}`} 
                      alt={relatedPost.title}
                      className="w-full h-full object-cover transition-transform hover:scale-105 duration-500" 
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-medium text-gray-900 hover:text-primary transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

export default PostDetail;